<template>
    <div id="product_menu">
        <section class="menu-ctrl">
            <span class="menu-ctrl__text">{{ $t('__map_obs.top_hint') }}
                <span v-show="!is_get_complete_webmap">
                    <v-progress-circular
                        indeterminate
                        size="18"
                        width="2"
                        color="#2366f8"
                    />
                    {{ $t('__map_obs.is_loading') }}
                </span>
            </span>
            <!-- 選擇圖資 右側的兩個icon -->
            <div class="icon-container">
                <QpeTooltip>
                    <template #hovered_item>
                        <v-icon 
                            color="white"
                            @click="close_all"
                        >
                            icon-unfold-less
                        </v-icon>
                    </template>
                    <template #tooltip_content>
                        <span>{{ $t('__map_obs.close_all_tree') }}</span>
                    </template>
                </QpeTooltip>
                <QpeTooltip>
                    <template #hovered_item>
                        <v-icon 
                            color="white"
                            @click="cancel_all"
                        >
                            icon-cancle-select                        
                        </v-icon>
                    </template>
                    <template #tooltip_content>
                        <span>{{ $t('__map_obs.cancel_all') }}</span>
                    </template>
                </QpeTooltip>
            </div>
        </section>
        <div
            id="map_obs"
            class="map-obs"
            :class="{ 'eng-height': i18n_language === 'en' }"    
        >
            <TreeView
                v-if="init_items.length > 0"
                :name="'map_obs'"
                ref="child_treeview"
                :current_tab="'forecast_observation'"
                :init_items="init_items"
                :checked_selectors="checked_selectors"
                :init_opened_layers="[]"
                :check_function="_cancel_timeless_items"
                :special_list="special_list"
                :selected_product_count_callback="selected_product_count_callback"
                :slider_ticks_mapping="slider_ticks_mapping"
                @control_special_list="_control_special_checkboxes"
            />
        </div>
    </div>
</template>


<script>
    import { 
        get_webmap_ajax, 
        get_webmap_json_init_ajax, 
        get_webmap_past_ajax, 
        update_time_ajax } from '@/utils/emap/emap_init.js';
    import { submit_ajax_by_case } from '@/utils/case/case_ajax.js';
    import { mapState, mapMutations, mapGetters } from "vuex";
    import isEqual from 'lodash/isEqual';
    import intersection from 'lodash/intersection';
    import { MENU_ICON, EXISTING_MENU_SLIDER_GROUP_LIST } from '@/config/menu_config.js';
    import TreeView from '@/components/tree_view.vue';

    export default {
        name: 'ForecastObservation',

        components: { 
            TreeView,
            QpeTooltip: () => import('@/components/tool/qpe_tooltip.vue'),
        },

        data() {
            return {
                wss_connection: null, // wss連線資料會建立在這
                ws_valid_timestamp: new Map(), //透過wss回傳時間判定webjson裡面的資料時間是否有超出
                webmap_data_obj: {},
                is_get_complete_webmap: false,
                data_delay_t: 1000 * 65, //資料移機需要時間, 需延後處理資料顯示 
                initialize_complete: false,

                init_opened_layers: [], // 預設右側導覽列要打開的選項
                init_items: [], // 右側導覽列選單資料
                checked_selectors: {},
                selected_items: [],
                newest_data: {},

                related_group_object: {},
                cancel_list: [],
                timeless_list: [],

                interval_func: 0,
                update_interval: 10000,
                id_set_timeout: -1,
                is_trigger: false,

                data_group_record: {},
                slider_ticks_mapping: {},
            }
        },

        computed: {
            ...mapState([
                'client_name',
                'language',
                'i18n_language',
            ]),

            ...mapState('emap', [
                'accumlated_precp_id_list',
                'user_auto_update_setting',
                'is_timer_on',
                'time_choose_parameters',
                'current_time_epoch',
                'first_time_render',
                'multiple_group_list',
                'has_got_map_config_from_server'
            ]),

            ...mapState('emap', {
                selected_products_global: 'selected_products'
            }),

            ...mapState('emap-monitor', [
                'is_monitor_open_emap',
            ]),

            ...mapState('case', [
                'case_selected',
            ]),

            ...mapGetters([
                'api_language_parameters'
            ]),

            related_group_list() {
                return Object.keys(this.related_group_object)
            },

            special_list() {
                return [...this.related_group_list, ...this.cancel_list]
            },

            // 目前所選擇圖資的 data_name 清單
            selected_items_data_name_list() {
                return this.selected_items.map(x => x.data_name);
            },

            is_multiple_products_activating() {
                const is_active = this.multiple_group_list.filter(o=>{
                    return o.list.every(ID=>this.selected_items_data_name_list.includes(ID))
                })
                return is_active.length > 0 ? true : false
            },

            is_forecast_products_activating() {
                const forecast_list = ['id_37', 'id_38', 'id_77']
                const is_active = this.selected_items_data_name_list.some(ID=>{
                    return forecast_list.includes(ID)
                })
                return is_active
            },
        },

        watch: {
            //原先用language, 但是TESTBED的webjson init比地圖設定檔案還快回來, 所以改監測地圖設定檔案
            has_got_map_config_from_server: {
                immediate: true,
                async handler() {
                    if (!this.has_got_map_config_from_server) return

                    this.set_first_time_render(true)
                    const { status: map_time_status } = this.time_choose_parameters
                    if (map_time_status === 'newest') {
                        await this.initialize()
                        // 剛進入頁面先去執行 initialize() 中的 get_webmap_json_init_ajax() 取得初始資料，讓右側導覽列先出現
                    }

                    if (map_time_status === 'history') {
                        const { start_time_epoch, end_time_epoch } = this.time_choose_parameters
                        const response = await get_webmap_past_ajax(this.client_name, start_time_epoch, end_time_epoch, this.api_language_parameters);
                        await this._init_totally(response)
                    }

                    await this.update_automatically()
                    // 接著馬上先跑一次 update_automatically() 自動更新，不然取初始資料跟原本的自動更新之間會有十幾秒的時間差
                    // 這邊如果不跑update_automatically()，使用者在這期間選產品會抓不到資料
                }
            },

            time_choose_parameters: {
                handler(new_value, old_value) {
                    if (JSON.stringify(new_value) === JSON.stringify(old_value)) return

                    const {
                        status: map_time_status,
                        start_time_epoch,
                        end_time_epoch
                    } = new_value

                    this.set_first_time_render(true)
                    if (map_time_status === 'newest') {
                        return this.update_newest_data_time()
                    }

                    if (map_time_status === 'history') {
                        if (this.wss_connection) {
                            console.warn('map_time_status: history, 關閉ws連線');
                            this.wss_connection.close(); // 關閉ws連線
                            this.wss_connection = null; // 清空裝ws連線物件的變數
                        }
                        return this.update_data_time(start_time_epoch, end_time_epoch)
                    }

                    if (map_time_status === 'case') {
                        if (this.wss_connection) {
                            console.warn('map_time_status: case, 關閉ws連線');
                            this.wss_connection.close(); // 關閉ws連線
                            this.wss_connection = null; // 清空裝ws連線物件的變數
                        }
                        return this.update_data_by_case()
                    }  
                }
            },

            user_auto_update_setting() {
                // user 點擊時立即觸發一次
                this.user_auto_update_setting && this.update_webmap_process(this.update_automatically)
            },

            is_timer_on: {
                handler() {
                    this.check_timer();
                }
            },

            is_monitor_open_emap() {
                !this.is_monitor_open_emap && this.show_emap_data()
            },

            selected_items_data_name_list: {
                handler(newV, oldV) {
                    const {status: map_time_status} = this.time_choose_parameters;

                    if (JSON.stringify(newV) != JSON.stringify(oldV) && map_time_status === 'newest' && this.initialize_complete) {
                        if (this.wss_connection) {
                            console.warn('selected_items_data_name_list觸發, 關閉ws連線', newV);
                            this.wss_connection.close(); // 關閉ws連線
                            this.wss_connection = null; // 清空裝ws連線物件的變數
                        }
                        if (newV.length > 0) this.open_wss_connection(); // 有選擇圖資時才開啟ws連線
                    }
                },
                deep: true
            },

            initialize_complete: {
                handler(newV, oldV) {
                    console.warn('[initialize_complete]',newV);
                    if (newV != oldV && newV) {
                        if (this.wss_connection) {
                            this.wss_connection.close(); // 關閉ws連線
                            this.wss_connection = null; // 清空裝ws連線物件的變數
                        }
                        if (this.selected_items_data_name_list.length > 0) this.open_wss_connection(); // 有選擇圖資時才開啟ws連線
                    }
                },
                deep: true
            },

            webmap_data_obj: {
                handler(newV, oldV) {
                    const all_3D_products_list = ['id_127', 'id_370', 'id_371'];
                    // id_127 雷達回波3D
                    // id_370 WISSDOM風場
                    // id_371 RWRF短期預報
                    let obs_data_3D_products = newV.data.obs_data.filter(item => all_3D_products_list.includes(item.data_name));
                    let forecast_data_3D_products = newV.data.forecast_data.filter(item => all_3D_products_list.includes(item.data_name));

                    let all_3D_products = [...obs_data_3D_products, ...forecast_data_3D_products];
                    this.set_all_3D_products(all_3D_products);
                },
                deep: true
            },
        },

        methods: {
            ...mapMutations([
                'set_i18n_language'
            ]),

            ...mapMutations('emap', [
                'set_selected_products',
                'set_selected_timeless_item_id',
                'set_is_clear_all',
                'set_emap_special_feature_list',
                'set_accumlated_precp_id_list',
                'set_is_show_accumulated_precp_table',
                'set_current_time_epoch',
                'set_first_time_render',
                'set_all_3D_products'
            ]),

            ...mapMutations('case', [
                'set_all_cases',
            ]),

            async initialize() {
                try {
                    const response = await get_webmap_json_init_ajax(this.client_name, this.api_language_parameters);
                    const webmap_data_obj = this._filter_ws_valid_data(response)
                    this.webmap_data_obj = webmap_data_obj
                    await this._init_totally(response)

                    this.initialize_complete = true
                } catch (err) {
                    console.error(err)
                }
                this.set_first_time_render(false)
            },

            async update_newest_data_time() {
                const response = await get_webmap_ajax(this.client_name, this.api_language_parameters);
                this.webmap_data_obj = JSON.parse(JSON.stringify(response));
                this._update_only_timeseries(response)

                // 從個案切回來的狀態處置
                this.$nextTick( () => {
                    this.set_first_time_render(false)
                })
            },
            
            async update_data_time(start_time_epoch, end_time_epoch) {
                const response = await get_webmap_past_ajax(this.client_name, start_time_epoch, end_time_epoch, this.api_language_parameters);
                this._update_only_timeseries(response)

                // 從個案切回來的狀態處置
                this.$nextTick( () => {
                    this.set_first_time_render(false)
                })
            },

            async update_automatically(ws_data = null) {
                console.warn('[update_automatically]');
                this.set_first_time_render(true)

                let webmap_data_obj = {}

                //websocket更新
                if(!this.is_forecast_products_activating && !this.is_multiple_products_activating && Object.keys(this.webmap_data_obj).length && ws_data){
                    console.warn('[concat websocket data]', ws_data);
                    const response = this._concat_ws_data(ws_data)
                    webmap_data_obj = this._filter_ws_valid_data(response)
                }
                //webjson更新
                else{
                    console.warn('[get webmap json]');
                    const response = await get_webmap_ajax(this.client_name, this.api_language_parameters);
                    webmap_data_obj = this._filter_ws_valid_data(response)
                    this.is_get_complete_webmap = true
                }
                this.webmap_data_obj = webmap_data_obj

                const all_need_webmap_data = this._get_untreated_data(JSON.parse(JSON.stringify(webmap_data_obj)))
                this._reconstruct_webmap_json(all_need_webmap_data)

                const new_selected_items = this.selected_items.map(item => {
                    return { 
                        ...item, 
                        ...this.newest_data[item.data_name] 
                    };
                })
                
                if (this._should_not_repaint_map(new_selected_items)) return;
                this.selected_items = new_selected_items;
                this.set_selected_products(new_selected_items);
            },

            _concat_ws_data(ws_data){
                // websocket data 合併 webmap json
                let webmap_data = JSON.parse(JSON.stringify(this.webmap_data_obj));
                ['obs_data', 'forecast_data', 'accumulate_data'].forEach(data_type => {
                    if(ws_data[data_type]){
                        ws_data[data_type].forEach((d) => {
                            if( !webmap_data.data || !webmap_data.data[data_type]) return
                            
                            const webjson_idx = webmap_data.data[data_type].findIndex(o=>o.data_name===d.data_name)
                            if(webjson_idx === -1) return

                            const webjson_data = webmap_data.data[data_type][webjson_idx]
                            const merged = { ...webjson_data, ...d }
                            webmap_data.data[data_type][webjson_idx] = merged
                        })}
                    }
                    
                )
                return webmap_data
            },

            _filter_ws_valid_data(webmap_data_response){
                //過濾掉比websocket時間更新的產品, 避免後端還未搬好資料
                let webmap_data = JSON.parse(JSON.stringify(webmap_data_response));
                if(!this.ws_valid_timestamp.size) return webmap_data

                // 過濾超出ws給的時間資料
                ;['obs_data', 'forecast_data', 'accumulate_data'].forEach(data_type => {
                    webmap_data.data[data_type].forEach((d, i) => {
                        if(!this.ws_valid_timestamp.has(d.data_name)) return

                        //id_37|38產品不過濾
                        if(['id_37','id_38'].includes(d.data_name)) return console.log(`${d.data_name} 為不使用websocket產品, 不過濾`)

                        //timeless_list產品資料無data_time_timestamp & 只會有一筆 => 不過濾
                        if(this.timeless_list.includes(d.data_name)) return console.log(`${d.data_name} 為timeless產品, 不過濾`)

                        const ws_valid_t = this.ws_valid_timestamp.get(d.data_name)
                        const invalid_idx = d.timestamp_list.findIndex(t => t > ws_valid_t)
                        if(invalid_idx < 0) return //皆符合時間

                        console.log(`${d.data_name} ws允許的時間為 ${ws_valid_t}`);
                        webmap_data.data[data_type][i].data_file = d.data_file.slice(0, invalid_idx)
                        webmap_data.data[data_type][i].timestamp_list = d.timestamp_list.slice(0, invalid_idx)
                    })
                })

                return webmap_data
            },

            async update_data_by_case() {
                // 處理個案時相當於重整整個網站，原因是選單要整個改掉
                const response = await submit_ajax_by_case(this.client_name, this.case_selected.case_id)
                this._update_only_timeseries(response)
            },

            _init_totally(response) {
                const all_need_webmap_data = this._get_untreated_data(response)
                this.set_all_cases(all_need_webmap_data['case_list']); // case list只用第一次進網頁時，webmap.json的資料
                this._reconstruct_webmap_json(all_need_webmap_data)
                this.set_current_time_epoch(all_need_webmap_data.current_time_epoch);
                this.set_i18n_language(this.language)
                this.show_emap_data();
                this.transfer_information(all_need_webmap_data)
            },

            _update_only_timeseries(response) {
                const all_need_webmap_data = this._get_untreated_data(response)
                this._reconstruct_webmap_json(all_need_webmap_data)
                this.show_emap_data()
            },

            _get_untreated_data(response) {
                const data = typeof response.data === 'object' ? response.data : JSON.parse(response.data); 
                return {
                    ...data, 
                    merged_data: [...data['obs_data'], ...data['forecast_data'], ...data['accumulate_data']],
                    menu_slider_group_list: data['menu_slider_group_list'] ?? EXISTING_MENU_SLIDER_GROUP_LIST
                }
            },

            transfer_information(all_need_webmap_data) {
                const { information } = all_need_webmap_data
                this.$emit('transfer-information', information);
            },

            _reconstruct_webmap_json(all_need_webmap_data) {
                const { merged_data } = all_need_webmap_data
                merged_data.forEach(datum => {
                    this.newest_data[datum.data_name] = datum;
                })

                if (this.first_time_render) {
                    this.init_opened_layers = [...new Set(merged_data.map(obs_datum => obs_datum['data_category']))]
                    this._assign_once_data(all_need_webmap_data);

                    this.data_group_record = this._get_data_group_record(merged_data)
                }
                this.init_items = this._generate_init_items(all_need_webmap_data);
                this.init_opened_layers = this.init_opened_layers.map(x => x + '_root');
                this.checked_selectors = this._generate_checked_property(all_need_webmap_data);
            },

            _assign_once_data(data) {
                this.related_group_object = data['related_group'] || {};
                this.cancel_list = data['cancel_list'] || [];
                this.timeless_list = data['timeless_list'] || [];
                this.update_interval = parseInt(data['user_frequency'], 10) || 10000
                
                this.set_accumlated_precp_id_list(data['accumlated_precp_list'] || [])
                this.set_emap_special_feature_list({
                    config: data['config'],
                    multiple_group_list: data['multiple_group_list'],
                    keep_group_list: data['keep_group_list'],
                    timeless_list: this.timeless_list,
                })
            },

            _get_data_group_record(merged_data) {
                return merged_data.reduce((accumulator, { data_group }) => {
                    if (!data_group) return accumulator

                    const data_group_count = accumulator[data_group]
                    if (data_group_count) {
                        return {
                            ...accumulator,
                            [data_group]: data_group_count + 1
                        }   
                    }
                    return {
                        ...accumulator,
                        [data_group]: 1
                    }
                }, {})
            },

            _generate_init_items(all_need_webmap_data) {
                const { merged_data } = all_need_webmap_data
                const items_groupby_layer1 = this.init_opened_layers.map(name => {
                    return {
                        'id': name + '_root',
                        'name': name, 
                        'values': []
                    }
                })

                merged_data.forEach(datum => {
                    const { data_category } = datum
                    const order = this.init_opened_layers.indexOf(data_category);
                    if (order >= 0) {
                        const values = items_groupby_layer1[order]['values']
                        items_groupby_layer1[order]['values'] = [...values, datum];
                    }
                })

                return this.init_opened_layers.map((layer_name, i) => {
                    const results_groupby_layer1 = this._generate_layer2and3(items_groupby_layer1[i]['values'], all_need_webmap_data);
                    const main_item = {
                        'id': layer_name + '_root',
                        'name':layer_name, 
                        'node':true, 
                        'layer1':true, 
                        'radio_name': `${layer_name}root`,
                        'children': results_groupby_layer1,
                        'icon': MENU_ICON[layer_name]
                    }

                    return main_item
                })
            },

            _generate_layer2and3(layer1_group_data, { information, menu_slider_group_list }) { //同一個category的
                let ary = [];
                let layer2_list = [];
                layer1_group_data.forEach(datum => {
                    const {
                        data_name: id,                  // "id_79"
                        data_source: layer2_name,       // "計畫路徑"
                        data_description: layer3_name,  // ""
                        data_group                      // ""
                    } = datum

                    const {
                        menu_slider_group_index,    //第幾組slider
                        menu_slider_group,          //該組slider obj
                        menu_slider_group_id        //製造出該組slider id
                    } = this._get_menu_slider_group_info(menu_slider_group_list, id)

                    const is_cancel_item = this.cancel_list.includes(id)
                    const is_data_group_item = this.data_group_record?.[data_group] > 1
                    const menu_type = is_cancel_item || is_data_group_item ? 'optional_radio' : 'checkbox';
                    const information_group_by_layer2 = information.filter(({ data_source }) => data_source === layer2_name) 

                    let for_treeview_obj = {
                        'id': datum['data_name'],               // "id_79"
                        'name': layer2_name,                    // "計畫路徑"
                        'node': false, 
                        'layer1': false, 
                        'type': menu_type,                      // 'checkbox'
                        'radio_name': datum['data_category'],   // "投落送" 
                        data_group,                             // ""
                        'data_tag': datum['data_tag'],          // "計畫路徑"
                        'index': 0,
                    };

                    // TODO
                    // 如果出現非預期的 menu_slider 產品 id、跑這裡邏輯會壞
                    // 例如：累積雨量分布 + 屏東的麥卡托
                    if (menu_slider_group) {
                        this.slider_ticks_mapping[id] = menu_slider_group["dict"][for_treeview_obj.id];
                        const menu_hint_text = this.language === 'en' ? menu_slider_group?.hint_en : menu_slider_group?.hint || ''
                        const slider_group_length = Object.keys(menu_slider_group.dict).length
                        const is_all_slider_in_a_data_group = this.data_group_record?.[data_group] === slider_group_length
                        const slide_obj = { id }

                        if (!(layer2_list.includes(layer2_name))) {
                            layer2_list.push(layer2_name);
                            ary.push({
                                'id': menu_slider_group_id,
                                'name':layer2_name, 
                                'node':false, 
                                'layer1':false, 
                                'type': 'slider',
                                'sliders': [slide_obj],
                                'sliders_checkbox_style': is_all_slider_in_a_data_group ? 'checkbox' : 'radio',
                                'data_tag': menu_slider_group_id,
                                data_group,
                                'information': information_group_by_layer2,
                                'hint': menu_hint_text,
                            });
                        } else { //已有此layer2  直接push
                            const order = layer2_list.indexOf(layer2_name);
                            ary[order]['sliders'] = [...ary[order]['sliders'], slide_obj];
                        }
                    } else if (layer3_name) { //有layer3 表示是第三層的項目
                        for_treeview_obj['name'] = layer3_name;
                        for_treeview_obj['radio_name'] = layer2_name
                        //沒有此layer2 因此要新建
                        if (!layer2_list.includes(layer2_name)) {
                            layer2_list.push(layer2_name);
                            ary.push({
                                'id': `${layer2_name}_layer2`,
                                'name':layer2_name, 
                                'node':true, 
                                'layer1':false, 
                                'children':[for_treeview_obj],
                                'information': information_group_by_layer2
                            });
                        } else { //已有此layer2  直接push
                            const order = layer2_list.indexOf(layer2_name);
                            for_treeview_obj['index'] = ary[order]['children'].length;
                            ary[order]['children'].push(for_treeview_obj);
                        }
                    } else {
                        for_treeview_obj['index'] = layer2_list.length;
                        for_treeview_obj['information'] = information_group_by_layer2
                        ary.push(for_treeview_obj);   
                        layer2_list.push(layer2_name);    
                    }
                })
                return ary;
            },

            _generate_checked_property({ merged_data, information, menu_slider_group_list }) {
                return merged_data.reduce((accumulator, item) => {
                    const {
                        data_category: first_layer_name,
                        data_source: second_layer_name,
                        data_description: third_layer_name,
                        default_checked,
                        data_name: id,
                    } = item

                    const path_name = !third_layer_name ? `${first_layer_name}/${second_layer_name}` : `${first_layer_name}/${second_layer_name}/${third_layer_name}` 
                    const information_group_by_layer2 = information.filter(({ data_source }) => data_source === second_layer_name) 
                    
                    const slider_property = this._construct_slider_property(accumulator, item, menu_slider_group_list)
                    accumulator = {
                        ...accumulator,
                        ...slider_property,
                    }
                    
                    const checked_property = {
                        path_name,
                        'information': information_group_by_layer2,
                    }

                    if (this.checked_selectors[id] === undefined) {
                        return {
                            ...accumulator, 
                            [id]: { 
                                checked: default_checked,
                                ...item, 
                                ...checked_property,
                            }
                        }
                    }

                    // Object.assign 的先後順序不能換
                    return {
                        ...accumulator, 
                        [id]: { 
                            ...this.checked_selectors[id], 
                            ...item, 
                            ...checked_property 
                        }
                    }
                }, {})
            },

            _construct_slider_property(accumulator, item, menu_slider_group_list) {
                const { 
                    data_name: id,
                    data_group,
                } = item

                const {
                    menu_slider_group,
                    menu_slider_group_id
                } = this._get_menu_slider_group_info(menu_slider_group_list, id)

                if (!menu_slider_group) return {}

                const is_check_selector_exist = this.checked_selectors[menu_slider_group_id]
                if (is_check_selector_exist) {
                    return {
                        [menu_slider_group_id]: {
                            checked: this.checked_selectors[menu_slider_group_id].checked,
                            tick: this.checked_selectors[menu_slider_group_id].tick,
                            data_tag: menu_slider_group_id,
                            data_group,
                        }
                    }  
                }

                const is_first_slider_item = !accumulator[menu_slider_group_id]
                if (is_first_slider_item) {
                    return {
                        [menu_slider_group_id]: this._create_menu_slider_selector_property(menu_slider_group_id, item)
                    } 
                } else {
                    const new_selector_property = this._update_menu_slider_selector_property(accumulator, menu_slider_group_id, item)
                    return {
                        [menu_slider_group_id]: {
                            ...accumulator[menu_slider_group_id],
                            ...new_selector_property
                        }
                    }
                }
            },

            _get_menu_slider_group_info(menu_slider_group_list, id) {
                // id              // "id_79"
                // layer2_name,    // "計畫路徑"
                // layer3_name,    // ""
                // data_group      // ""

                let menu_slider_group_index = -1;
                menu_slider_group_list.forEach((x, index) => {
                    if (Object.entries(x.dict).filter(x => x.includes(id)).length > 0) menu_slider_group_index = index;
                })
                const menu_slider_group = menu_slider_group_list[menu_slider_group_index]
                const menu_slider_group_id = `slider_${menu_slider_group_index}`

                return {
                    menu_slider_group_index,    //第幾組slider
                    menu_slider_group,          //該組slider obj
                    menu_slider_group_id        //製造出該組slider id
                }
            },

            _create_menu_slider_selector_property(menu_slider_group_id, item) {
                const { data_name, data_group, default_checked } = item
                const slider_property = {
                    checked: default_checked,
                    tick: data_name,
                    data_tag: menu_slider_group_id,
                    data_group,
                }
                return slider_property
            },

            _update_menu_slider_selector_property(accumulator, menu_slider_group_id, item) {
                const {
                    data_name: id,
                    default_checked 
                } = item
                
                const is_need_change_tick = !accumulator[menu_slider_group_id].checked && default_checked
                accumulator[menu_slider_group_id] = {
                    ...accumulator[menu_slider_group_id],
                    checked: accumulator[menu_slider_group_id].checked 
                        ? accumulator[menu_slider_group_id].checked 
                        : default_checked,
                    tick: (is_need_change_tick)
                        ? id
                        : accumulator[menu_slider_group_id].tick,
                }

                return accumulator
            },

            update_selected_products(operation) {
                const new_selected_items = this._generate_selected_items();
                if (new_selected_items.length == 0 && operation == 'show') return;
                
                if (this._should_not_repaint_map(new_selected_items)) return;
                this.selected_items = new_selected_items;
                this.set_selected_products(new_selected_items);
            },

            _generate_selected_items() {        
                return Object.entries(this.checked_selectors)
                    .filter(([key, selector]) => key.includes('id_') && selector.checked)
                    .map(item => item[1])
            },
            
            _should_not_repaint_map(new_selected_items) {
                const conditions_to_dispatch_selected_product = ({ data_file, data_tag }) => ({ data_file, data_tag })
                const old_condition = this.selected_items.map(conditions_to_dispatch_selected_product);
                const new_condition = new_selected_items.map(conditions_to_dispatch_selected_product);

                if (!isEqual(new_condition, old_condition)) return
                
                // 此情境用於地圖與監控表格互動功能關閉時，重新繪製目前選單的產品
                const global_condition = this.selected_products_global.map(conditions_to_dispatch_selected_product)
                return isEqual(new_condition, global_condition)
            },

            close_all() {
                this.$refs.child_treeview.close_all_trees();
            },

            cancel_all() {
                // 隱藏自動更新按鈕
                this.set_is_clear_all(true)

                // 左側選單取消全選
                Object.values(this.checked_selectors).forEach(selector => {
                    this.$set(selector, 'checked', false);
                });

                // 更新資料
                this.update_selected_products('clear');

                // 打開設定
                this.$emit('toggle_time_choose', true);

                // 清除 timeless_items
                this.set_selected_timeless_item_id('')

                this._set_accumulated_precp();
            },

            show_emap_data() {
                this._set_accumulated_precp();
                this._control_timeless_item();

                this.set_is_clear_all(false)
                this.update_selected_products('show');

                this.trigger_related_products_in_background();
            },

            // 開/關累積雨量
            _set_accumulated_precp() {
                if (this.accumlated_precp_id_list.length === 0) return;
                const accumlated_precp_id = this.accumlated_precp_id_list[0]
                this.set_is_show_accumulated_precp_table(this.checked_selectors[accumlated_precp_id]?.checked)
            },

            _control_timeless_item() {
                // 如果在timeless_items裡面的, 就要例外處理'
                const chosen_timeless_id = this.timeless_list.find(id => {
                    return this.checked_selectors[id]?.['checked']
                }) ?? ''

                this.set_selected_timeless_item_id(chosen_timeless_id)
            },
          
            _control_special_checkboxes(idx, node_name, name, id) {
                // 範例: node_name = 颱風資訊, name = 颱風路徑資訊
                const status = this.checked_selectors[id]['checked']
                this.$set(this.checked_selectors[id], 'checked', status);

                // 同時打開其他產品
                if(this.related_group_list.includes(id)) {
                    this._trigger_related_group(id, status);
                }

                // 取消所有選項，勾他取消其他全部，勾其他取消他
                if(this.cancel_list.includes(id)) {
                    // 與其他動作相比，不刪除 timeless_items
                    this._control_cancel_items(id, status);
                } else {
                    this._cancel_timeless_items()
                }
            },

            _control_cancel_items(id, status) {
                if (status) {
                    this._cancel_all_item_besides_checked_item(id)
                    this._disable_map_setting_click(false);
                } else {
                    this._disable_map_setting_click(true);
                }
            },

            _cancel_all_item_besides_checked_item(checked_item_id) {
                Object.keys(this.checked_selectors).forEach(id => {
                    id !== checked_item_id && this.$set(this.checked_selectors[id], 'checked', false);
                });
            },

            _trigger_related_group(id, status) {
                this._disable_map_setting_click(true);
                const all_trigger_ids = this.related_group_object[id]
                if (status && all_trigger_ids.length > 0) {
                    const emap_menu_ids = Object.keys(this.checked_selectors)
                    const emap_menu_trigger_ids = intersection(all_trigger_ids, emap_menu_ids)
                    emap_menu_trigger_ids.forEach(id => this.$set(this.checked_selectors[id], 'checked', true))

                    const slider_data_triggered = all_trigger_ids.map(id => {
                        const { data_name, data_group } = this.checked_selectors[id]
                        return { id: data_name, data_group }
                    })

                    Object.entries(this.checked_selectors)
                        .filter(([key]) => key.includes('slider'))
                        .forEach(([, slider_selector]) => {
                            const { data_group } = slider_selector
                            // 如果 related_group 胡亂設定多個 slider，這邊只找第一個
                            const datum_triggered = slider_data_triggered.find(datum => datum.data_group === data_group)
                            
                            if (datum_triggered) {
                                this.$set(slider_selector, 'tick', datum_triggered.id)
                                this.$set(slider_selector, 'checked', true)
                            }
                        })
                }
            },

            // 勾其他選項，刪除 timeless_items
            _cancel_timeless_items() {
                this._disable_map_setting_click(true);
                this.timeless_list.forEach(id => {
                    this.checked_selectors[id] && this.$set(this.checked_selectors[id], 'checked', false);
                })
            },

            _disable_map_setting_click(should_disable) {
                this.$emit('toggle_time_choose', should_disable);
            },

            selected_product_count_callback() {
                return ((accumulator, selector) => {
                    const { data_category, checked } = selector
                    const current_checked_count = accumulator[data_category] ?? 0
                    const new_checked_count = checked ? current_checked_count + 1 : current_checked_count
                    return {
                        ...accumulator, 
                        ...{ [data_category]: new_checked_count }
                    }
                })
            },
            
            trigger_related_products_in_background() {
                Object.values(this.checked_selectors)
                    .filter(({ checked }) => checked)
                    .map(({ data_name }) => data_name)
                    .forEach(id => {
                        const related_ids = this.related_group_object[id]
                        related_ids?.length > 0 && this.$emit('check-background-menu-from-emap-menu', related_ids)
                    })
            },

            check_timer() {
                if (this.is_timer_on) {
                    if (!this.interval_func) {
                        this.interval_func = setInterval(() => {
                            if(this.wss_connection) return 

                            console.warn('未啟動 wss_connection, 開始計時更新');
                            this.update_webmap_process(this.get_update_automatically_has_latency_function)
                        }, this.update_interval);
                    }
                } else {
                    clearInterval(this.interval_func);
                    this.interval_func = null

                    clearTimeout(this.id_set_timeout)
                    this.is_trigger = false
                    this.id_set_timeout = -1
                }
            },
            
            async get_update_automatically_has_latency_function() {
                /* 
                    因為後端的檔案產出與資料庫寫入會不同步
                    所以需要等待一小段延遲時間去呼叫自動更新操作
                */
                if (this.is_trigger) return

                this.is_trigger = true
                const deley_t = this.data_delay_t
                await this.delay(deley_t)

                this.is_trigger = false
                this.update_automatically()
            },
            
            async update_webmap_process(update_callback) {
                console.warn('[update_webmap_process] 檢查webmap time是否更新');
                const response = await update_time_ajax(this.client_name);
                const { user_updatetime } = typeof response.data === 'object' ? response.data : JSON.parse(response.data); 
                if (user_updatetime !== this.current_time_epoch) {
                    console.warn('[update_webmap_process] webmap time已更新');
                    update_callback()
                    this.set_current_time_epoch(user_updatetime);
                } 
            },

            // 開啟 websocket 連線
            open_wss_connection() {
                const ids = this.selected_items_data_name_list.map(x => x.split('id_')[1]).join(',');
                const host = window.location.host;
                // 本地端 ws url
                let url = `ws://localhost:23458/?ids=${ids}`;
                
                if (!host.includes('localhost:8088')) {
                    url = `wss://${host}/${this.client_name}/webmap/ws/display/?ids=${ids}`;
                }
               
                // websocket 連線
                this.wss_connection = new WebSocket(url);

                // 監聽連線狀態
                this.wss_connection.onopen = () => {
                    console.warn('open connection ', url)
                }
                this.wss_connection.onclose = (event) => {
                    this.ws_valid_timestamp.clear()
                    console.warn('重設 ws_valid_timestamp');
                    console.warn('close connection', event);
                }

                //接收 Server 發送的訊息
                this.wss_connection.onmessage = event => {
                    console.warn('[ websocket 更新 ]', new Date());
                    const ws_processing = async (event) => {
                        const ws_data = JSON.parse(event.data);

                        // 初始資料直接使用(initial_data)
                        // 非初始資料延遲50秒更新
                        const deley_t = this.data_delay_t
                        if(!ws_data.initial_data) await this.delay(deley_t);

                        console.warn('[ 開始處理 websocket 資料 ]' , ws_data)
                        this.set_ws_valid_timestamp(ws_data)
                        await this.update_automatically(ws_data)
                        console.warn('[ 結束處理 websocket 資料 ]')
                    };
                    ws_processing(event);
                }
            },
            delay(ms) {
                console.warn('[delay]', ms)
                return new Promise(resolve => setTimeout(resolve, ms));
            },
            set_ws_valid_timestamp(ws_data){
                ['obs_data', 'forecast_data', 'accumulate_data'].forEach(data_type => {
                    if(!ws_data[data_type]) return

                    ws_data[data_type].forEach(d => {
                        if(!d.timestamp_list) return console.warn('ws_data出現異常',ws_data, d);
                        const valid_t = d.timestamp_list.slice(-1)[0]
                        this.ws_valid_timestamp.set(d.data_name, valid_t)
                    })
                })
            }
        },      
    }
</script>

<style lang="scss" scoped>
    @import '../../../../css/mixin';

    .map-obs {
        overflow: auto;
        height: calc(100vh - 128px);
        background: #454d66;

        @include laptop {
            height: calc(var(--app-height) - 128px);
        }
    }

    .menu-ctrl {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 3px;
        background: var(--base-gray-darken);

        .icon {
            width: 24px;
            height: 24px;
            object-fit: contain;
            cursor: pointer;
        }

        &__text {
            margin-left: 1em;
            color: var(--grey-pipe);
        }
    }
</style>
